// 数字与时间的写法（DESIGN.md §5.4）。一处定义，各页共用，免得同一个数在两页写法不一样。

const NUM = new Intl.NumberFormat("zh-CN");

/** 千分位整数；空值写「—」 */
export function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return NUM.format(n);
}

/** 百分比，一位小数。传 0–1 的比例 */
export function fmtPct(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return "—";
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** 后端给的时间串（「2026-10-04 10:42:05」或 ISO）解析成本地显示用的各段。
 *  后端的时间已经是要显示的那个时区，这里**不做时区换算**，只拆字段。 */
function parts(value: string): { y: string; mo: string; d: string; h: string; mi: string; s: string } | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const [, y = "", mo = "", d = "", h = "", mi = "", s = "00"] = m;
  return { y, mo, d, h, mi, s };
}

/** 今年内 `10-04 10:42`，跨年 `2025-12-30 10:42` */
export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const p = parts(value);
  if (!p) return value;
  const sameYear = p.y === String(new Date().getFullYear());
  return `${sameYear ? "" : `${p.y}-`}${p.mo}-${p.d} ${p.h}:${p.mi}`;
}

/** 带秒：今年内 `10-04 10:42:05`，跨年 `2025-12-30 10:42:05`。对话记录每一轮的时间用 */
export function fmtDateTimeSec(value: string | null | undefined): string {
  if (!value) return "—";
  const p = parts(value);
  if (!p) return value;
  const sameYear = p.y === String(new Date().getFullYear());
  return `${sameYear ? "" : `${p.y}-`}${p.mo}-${p.d} ${p.h}:${p.mi}:${p.s}`;
}

/** 只要日期：今年内 `10-04`，跨年 `2025-12-30` */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const p = parts(`${value.slice(0, 10)} 00:00`);
  if (!p) return value;
  const sameYear = p.y === String(new Date().getFullYear());
  return `${sameYear ? "" : `${p.y}-`}${p.mo}-${p.d}`;
}

/** 时分秒 `10:42:05`，会话记录的时间列用 */
export function fmtClock(value: string | null | undefined): string {
  if (!value) return "—";
  const p = parts(value);
  return p ? `${p.h}:${p.mi}:${p.s}` : value;
}

const BJ = new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
});

/** 带时区的时间（UTC 的 ISO 串）换成北京时间的「2026-10-04 10:42:05」，再交给上面几个函数排版。
 *  `zh-CN` 默认输出 `2026/10/04`，所以用 formatToParts 自己拼（DESIGN.md §5.4） */
export function toBeijing(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(BJ.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
}

/** 1 分钟内「刚刚」，24 小时内「3 小时前」，更早写绝对时间 */
export function fmtRelative(value: string | null | undefined): string {
  if (!value) return "—";
  const t = new Date(value.replace(" ", "T")).getTime();
  if (Number.isNaN(t)) return value;
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} 小时前`;
  return fmtDateTime(value);
}

/** 毫秒写成 `1.2 s`；不到 1 秒写 `860 ms` */
export function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms)} ms`;
}

// 统一认证的五个角色（汤祺 2026-10-07；ops 的中文名是「履约」、staff 是「员工」，key 不变），按这个顺序列；
// 不认识的角色照原文排在后面
const ROLE_LABEL: Record<string, string> = { admin: "管理员", dev: "研发", support: "客服", ops: "履约", operator: "运营", staff: "员工" };
const ROLE_ORDER = Object.keys(ROLE_LABEL);

function roleRank(r: string): number {
  const i = ROLE_ORDER.indexOf(r);
  return i < 0 ? ROLE_ORDER.length : i;
}

export function roleLabel(roles: string[]): string {
  return [...roles].sort((a, b) => roleRank(a) - roleRank(b)).map((r) => ROLE_LABEL[r] ?? r).join(" · ");
}
